import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Col, Row, Spinner, Table } from "reactstrap";
import {
  IoCheckmarkCircleOutline,
  IoCashOutline,
  IoBanOutline,
  IoRefreshOutline,
  IoTrashOutline
} from "react-icons/io5";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import sidebar from "../../utils/sidebar";
import { del, get, post } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import { canTransitionTo, formatMoney, isEditable, printPayrollStatus } from "../../utils/payrollStatus";
import PayslipDrawer from "./Drawers/Payslip";

function StatCard({ label, value, tone }) {
  return (
    <Col md="3" className="mb-3">
      <div className="border rounded p-3 h-100">
        <div className="text-muted small text-uppercase fw-semibold mb-1">{label}</div>
        <div className={`fs-5 fw-bold ${tone || ''}`}>{value}</div>
      </div>
    </Col>
  );
}

function ViewPayrollRunPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatDate, settings } = useSettings();

  const [payrollRun, setPayrollRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState(null);
  const [selectedPayslipId, setSelectedPayslipId] = useState(null);

  const currency = payrollRun?.currency || settings?.sales?.base_currency || '';
  const editable = isEditable(payrollRun?.status);

  const getPayrollRun = async () => {
    const data = await get(`/hr/payroll-runs/view/${id}`);
    if (data?.success) {
      setPayrollRun(data.payroll_run);
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setLoading(false);
  };

  useEffect(() => {
    getPayrollRun();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateStatus = async (status) => {
    setWorking(true);
    const data = await post(`/hr/payroll-runs/update-status/${id}`, { status });
    if (data?.success) {
      await getPayrollRun();
      toast.success(t(`hr.payrollRun.marked.${status}`));
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setWorking(false);
  };

  const recalculate = async () => {
    setWorking(true);
    const data = await post(`/hr/payroll-runs/recalculate/${id}`, {});
    if (data?.success) {
      await getPayrollRun();
      toast.success(t('hr.payrollRun.recalculated'));
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setWorking(false);
  };

  const deleteRun = async () => {
    setWorking(true);
    const data = await del(`/hr/payroll-runs/delete/${id}`);
    if (data?.success) {
      toast.success(t('alerts.success_delete'));
      navigate('/hr/payroll');
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
      setWorking(false);
    }
  };

  const CONFIRMATIONS = {
    approved: {
      title: t('hr.payrollRun.approveTitle'),
      message: t('hr.payrollRun.approveMessage'),
      button: t('hr.payrollRun.approve'),
      color: 'success',
      run: () => updateStatus('approved')
    },
    paid: {
      title: t('hr.payrollRun.payTitle'),
      message: t('hr.payrollRun.payMessage'),
      button: t('hr.payrollRun.markPaid'),
      color: 'success',
      run: () => updateStatus('paid')
    },
    cancelled: {
      title: t('hr.payrollRun.cancelTitle'),
      message: t('hr.payrollRun.cancelMessage'),
      button: t('hr.payrollRun.cancelRun'),
      color: 'danger',
      run: () => updateStatus('cancelled')
    },
    deleted: {
      title: t('hr.payrollRun.deleteTitle'),
      message: t('hr.payrollRun.deleteMessage'),
      button: t('common.delete'),
      color: 'danger',
      run: deleteRun
    }
  };

  if (loading) {
    return (
      <SidebarLayout sidebar={sidebar}>
        <Spinner color="primary" className="d-block mx-auto my-5" />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('hr.payroll'), payrollRun?.reference]}
          onBack={() => navigate('/hr/payroll')}
          onReload={getPayrollRun}
        >
          {editable && (
            <DefaultButton size="sm" color="secondary" outline onClick={recalculate} disabled={working}>
              <IoRefreshOutline className="me-1" />
              {t('hr.payrollRun.recalculate')}
            </DefaultButton>
          )}
          {canTransitionTo(payrollRun?.status, 'approved') && (
            <DefaultButton size="sm" color="success" onClick={() => setActionToConfirm('approved')} disabled={working}>
              <IoCheckmarkCircleOutline className="me-1" />
              {t('hr.payrollRun.approve')}
            </DefaultButton>
          )}
          {canTransitionTo(payrollRun?.status, 'paid') && (
            <DefaultButton size="sm" color="success" onClick={() => setActionToConfirm('paid')} disabled={working}>
              <IoCashOutline className="me-1" />
              {t('hr.payrollRun.markPaid')}
            </DefaultButton>
          )}
          {canTransitionTo(payrollRun?.status, 'cancelled') && (
            <DefaultButton size="sm" color="danger" outline onClick={() => setActionToConfirm('cancelled')} disabled={working}>
              <IoBanOutline className="me-1" />
              {t('hr.payrollRun.cancelRun')}
            </DefaultButton>
          )}
          {editable && (
            <DefaultButton size="sm" color="danger" outline onClick={() => setActionToConfirm('deleted')} disabled={working}>
              <IoTrashOutline />
            </DefaultButton>
          )}
        </PageSubHeader>

        <div className="subheader-page-content scrollable">
          <Card className="mb-3">
            <CardBody>
              <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                <h5 className="fw-bold mb-0">{payrollRun?.name || payrollRun?.reference}</h5>
                {printPayrollStatus(payrollRun?.status)}
                <span className="text-muted small">
                  {formatDate(payrollRun?.period_start)} → {formatDate(payrollRun?.period_end)}
                </span>
              </div>

              <Row>
                <StatCard label={t('hr.payrollRun.employees')} value={payrollRun?.employees_count} />
                <StatCard label={t('hr.payrollRun.totalBasic')} value={formatMoney(payrollRun?.total_basic, currency)} />
                <StatCard
                  label={t('hr.benefits.totalDeductions')}
                  value={`-${formatMoney(payrollRun?.total_deductions, currency)}`}
                  tone="text-danger"
                />
                <StatCard
                  label={t('hr.payrollRun.totalPayout')}
                  value={formatMoney(payrollRun?.total_net, currency)}
                  tone="text-success"
                />
              </Row>

              {payrollRun?.status === 'draft' && (
                <p className="text-muted small mb-0">{t('hr.payrollRun.draftHint')}</p>
              )}
              {payrollRun?.status === 'approved' && (
                <p className="text-muted small mb-0">{t('hr.payrollRun.approvedHint')}</p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h5 className="fw-bold mb-3">{t('hr.payrollRun.payslips')}</h5>

              <Table responsive hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t('hr.payrollRun.reference')}</th>
                    <th>{t('hr.employeeName')}</th>
                    <th className="text-end">{t('hr.basicSalary')}</th>
                    <th className="text-center">{t('hr.payrollRun.paidDays')}</th>
                    <th className="text-end">{t('hr.benefits.totalAllowances')}</th>
                    <th className="text-end">{t('hr.benefits.totalDeductions')}</th>
                    <th className="text-end">{t('hr.benefits.netSalary')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!payrollRun?.payslips?.length && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        {t('hr.payrollRun.noPayslips')}
                      </td>
                    </tr>
                  )}
                  {payrollRun?.payslips?.map((payslip) => (
                    <tr
                      key={payslip.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedPayslipId(payslip.id)}
                    >
                      <td className="small text-muted">{payslip.reference}</td>
                      <td>
                        <div className="fw-semibold">{payslip.employee_name}</div>
                        <div className="small text-muted">
                          {[payslip.job_title, payslip.department].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </td>
                      <td className="text-end">{formatMoney(payslip.basic_salary)}</td>
                      <td className="text-center">{payslip.paid_days} / {payslip.period_days}</td>
                      <td className="text-end text-success">+{formatMoney(payslip.total_allowances)}</td>
                      <td className="text-end text-danger">-{formatMoney(payslip.total_deductions)}</td>
                      <td className="text-end fw-bold">{formatMoney(payslip.net_salary)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </div>
      </div>

      <PayslipDrawer
        payslipId={selectedPayslipId}
        isOpen={selectedPayslipId !== null}
        onClose={() => setSelectedPayslipId(null)}
        editable={editable}
        currency={currency}
        refreshRun={getPayrollRun}
      />

      <ConfirmationModal
        isOpen={actionToConfirm !== null}
        title={actionToConfirm ? CONFIRMATIONS[actionToConfirm].title : ''}
        message={actionToConfirm ? CONFIRMATIONS[actionToConfirm].message : ''}
        confirmationText={actionToConfirm ? CONFIRMATIONS[actionToConfirm].button : ''}
        confirmColor={actionToConfirm ? CONFIRMATIONS[actionToConfirm].color : 'danger'}
        onConfirm={() => CONFIRMATIONS[actionToConfirm].run()}
        toggle={() => setActionToConfirm(null)}
      />
    </SidebarLayout>
  );
}

export default ViewPayrollRunPage;
